/**
 * ZoomControls Component (Story 5.38, Task 2)
 *
 * Floating zoom control bar rendered at the bottom-left of the canvas area.
 * Contains zoom out (–), percentage display, zoom in (+), and fit-to-width controls.
 * Stays fully opaque at all times.
 */

import { motion } from "framer-motion";
import { Minus, Plus, ArrowsOut } from "@phosphor-icons/react";

export interface ZoomControlsProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToWidth: () => void;
  minZoom: number;
  maxZoom: number;
  /** Left offset in px (e.g. left sidebar width) — used to center on canvas */
  canvasLeftOffset?: number;
  /** Right offset in px (e.g. right sidebar width) — used to center on canvas */
  canvasRightOffset?: number;
}

export function ZoomControls({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onFitToWidth,
  minZoom,
  maxZoom,
  canvasLeftOffset = 0,
  canvasRightOffset: _canvasRightOffset = 0,
}: ZoomControlsProps) {
  return (
    <motion.div
      data-testid="zoom-controls"
      className="fixed bottom-4 z-[60] flex items-center gap-1 rounded-2xl bg-background shadow-md px-2 py-1"
      style={{
        border: "0.5px solid var(--hairline)",
        left: `calc(${canvasLeftOffset}px + 16px)`,
      }}
    >
      {/* Zoom out button */}
      <button
        type="button"
        data-testid="zoom-out-btn"
        onClick={onZoomOut}
        disabled={zoomLevel <= minZoom}
        className="p-2 cursor-pointer hover:bg-accent transition-colors rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Zoom out"
      >
        <Minus size={16} />
      </button>

      {/* Percentage display */}
      <span
        data-testid="zoom-level-display"
        className="text-[13px] font-medium min-w-[48px] text-center select-none"
      >
        {zoomLevel}%
      </span>

      {/* Zoom in button */}
      <button
        type="button"
        data-testid="zoom-in-btn"
        onClick={onZoomIn}
        disabled={zoomLevel >= maxZoom}
        className="p-2 cursor-pointer hover:bg-accent transition-colors rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Zoom in"
      >
        <Plus size={16} />
      </button>

      {/* Fit-to-width button */}
      <button
        type="button"
        data-testid="fit-to-width-btn"
        onClick={onFitToWidth}
        className="p-2 cursor-pointer hover:bg-accent transition-colors rounded-lg"
        aria-label="Fit to width"
      >
        <ArrowsOut size={16} />
      </button>
    </motion.div>
  );
}
